import React, { useEffect, useState } from 'react'
import { Form, Input, InputNumber, Select, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import { IMemberLevelDetail } from '@/model/types/member'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import Auth from '@/views/main/components/auth/auth'
import { addMemberLevel, getMemberLevelList, removeMemberLevel, updateMemberLevel } from '../api'
import SettingPage, {
    SettingGrid,
    SettingPanel,
    SettingPreview,
    SettingTableContainer,
} from '../member-setting/setting-page'
import { useMemberSettingAccess } from '../member-setting/setting-access'
import {
    buildAddMemberLevelParams,
    buildMemberLevelUpdate,
    canDeleteMemberLevel,
    getChangedMemberLevels,
    getMemberLevelName,
    getNextMemberLevelName,
    validateMemberLevelName,
    validateMemberLevelNameForSave,
    validateMemberLevels,
} from './member-level-utils'

import './member-level.scss'

interface LevelForm {
    name: string
    experience_threshold: number
    experience_deduction?: number
    expiration: number
}

interface Props {
    dialog?: boolean
    onChanged?: () => void
}

const expirationOptions = [
    { label: '三个月', value: 1 },
    { label: '半年', value: 2 },
    { label: '一年', value: 3 },
    { label: '永久', value: 0 },
]

const normalizeLevel = (level: IMemberLevelDetail): IMemberLevelDetail => ({
    ...level,
    name: level.name || getMemberLevelName(level.level),
})

const MemberLevel: React.FC<Props> = props => {
    const [form] = Form.useForm<LevelForm>()
    const [levels, setLevels] = useState<IMemberLevelDetail[]>([])
    const [originLevels, setOriginLevels] = useState<IMemberLevelDetail[]>([])
    const [visible, setVisible] = useState(false)
    const [saving, setSaving] = useState(false)
    const [adding, setAdding] = useState(false)
    const access = useMemberSettingAccess()
    const nextLevelName = getNextMemberLevelName(levels)
    const listReq = useRequest(getMemberLevelList, {
        onSuccess: res => {
            const formatted = (res || []).map(normalizeLevel)
            setLevels(formatted)
            setOriginLevels(formatted)
        },
        onError: e => $msg('error', e.message || '获取会员等级失败'),
    })

    useEffect(() => {
        if (listReq.data) {
            const formatted = listReq.data.map(normalizeLevel)
            setLevels(formatted)
            setOriginLevels(formatted)
        }
    }, [listReq.data])

    useEffect(() => {
        if (visible) {
            form.resetFields()
            if (nextLevelName) {
                form.setFieldsValue({ name: nextLevelName })
            }
        }
    }, [form, nextLevelName, visible])

    const updateLevelField = (
        id: number,
        key: 'experience_threshold' | 'experience_deduction' | 'expiration' | 'name',
        value: any,
    ) => {
        setLevels(prev => prev.map(level => (level.id === id ? { ...level, [key]: value } : level)))
    }

    const changedLevels = getChangedMemberLevels(levels, originLevels)
    const saveDisabled = access.readOnly || saving || !changedLevels.length

    const saveChangedLevels = async (targets: IMemberLevelDetail[] = changedLevels) => {
        if (access.readOnly || saving) return
        if (!targets.length) {
            $msg('warning', '会员等级没有变更')

            return
        }
        const error = validateMemberLevels(levels)
        if (error) {
            $msg('warning', error)

            return
        }
        if (changedLevels.some(item => !targets.some(target => target.id === item.id))) {
            $msg('warning', '请先保存或撤销其他等级的修改')

            return
        }
        for (const level of targets) {
            const origin = originLevels.find(item => item.id === level.id)
            if (!origin) return
            const otherNames = levels.filter(item => item.id !== level.id).map(item => item.name)
            // 未改名时（可能是历史长名称）只校验空值和重名，不卡 4 字/字符规则
            const nameError = validateMemberLevelNameForSave(level.name, origin.name, otherNames)
            if (nameError) {
                $msg('warning', nameError)

                return
            }
        }
        setSaving(true)
        try {
            for (const level of targets) {
                const origin = originLevels.find(item => item.id === level.id)
                if (!origin) continue
                await updateMemberLevel(buildMemberLevelUpdate(level, origin))
            }
            $msg('success', '保存成功')
            listReq.refresh()
            props.onChanged && props.onChanged()
        } catch (e) {
            $msg('error', e.message || '保存会员等级失败')
            listReq.refresh()
        } finally {
            setSaving(false)
        }
    }

    const onAdd = async () => {
        try {
            if (!nextLevelName) {
                $msg('warning', '最多支持五个连续的会员等级；历史等级异常请先核对')

                return
            }
            const values = await form.validateFields()
            const name = (values.name || '').trim()
            const nameError = validateMemberLevelName(
                name,
                levels.map(item => item.name),
            )
            if (nameError) {
                $msg('warning', nameError)

                return
            }
            const maxThreshold = levels.length ? Math.max(...levels.map(item => Number(item.experience_threshold))) : -1
            if (Number(values.experience_threshold) <= maxThreshold) {
                $msg('warning', '新增等级成长值门槛必须大于当前最高等级')

                return
            }
            setAdding(true)
            await addMemberLevel(buildAddMemberLevelParams({ ...values, name }, levels))
            $msg('success', '新增成功')
            setVisible(false)
            form.resetFields()
            listReq.refresh()
            props.onChanged && props.onChanged()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '新增会员等级失败')
        } finally {
            setAdding(false)
        }
    }

    const onDelete = (level: IMemberLevelDetail) => {
        showLayer(
            <SgModal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '156px' }}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    removeMemberLevel({ id: level.id, level: level.level })
                        .then(() => {
                            $msg('success', '删除成功')
                            listReq.refresh()
                            props.onChanged && props.onChanged()
                        })
                        .catch(e => $msg('error', e.message || '删除会员等级失败'))
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认删除会员等级
                        <span data-color-ff4343 data-margin-h-6>
                            {level.name}
                        </span>
                        吗？
                    </div>
                </div>
            </SgModal>,
        )
    }

    const columns: ColumnsType<IMemberLevelDetail> = [
        { title: '等级', dataIndex: 'level', width: 90, render: value => value + 1 },
        {
            title: '等级名称',
            dataIndex: 'name',
            width: 220,
            render: (_value: string, record) => (
                <Input
                    disabled={access.readOnly || saving || changedLevels.some(item => item.id !== record.id)}
                    value={record.name ?? getMemberLevelName(record.level)}
                    style={{ width: '100%' }}
                    placeholder="最多4个字符"
                    onChange={e => updateLevelField(record.id, 'name', e.target.value)}
                />
            ),
        },
        {
            title: '成长值门槛',
            dataIndex: 'experience_threshold',
            width: 220,
            render: (value, record) => (
                <InputNumber
                    disabled={access.readOnly || saving || changedLevels.some(item => item.id !== record.id)}
                    min={0}
                    precision={0}
                    value={value}
                    style={{ width: '100%' }}
                    onChange={nextValue =>
                        updateLevelField(record.id, 'experience_threshold', (nextValue as number) || 0)
                    }
                />
            ),
        },
        {
            title: '成长值扣减',
            dataIndex: 'experience_deduction',
            width: 180,
            render: (value, record) => (
                <InputNumber
                    disabled={access.readOnly || saving || changedLevels.some(item => item.id !== record.id)}
                    min={0}
                    precision={0}
                    value={value}
                    style={{ width: '100%' }}
                    onChange={nextValue =>
                        updateLevelField(record.id, 'experience_deduction', (nextValue as number) || 0)
                    }
                />
            ),
        },
        {
            title: '有效期',
            dataIndex: 'expiration',
            width: 160,
            render: (value, record) => (
                <Select
                    disabled={access.readOnly || saving || changedLevels.some(item => item.id !== record.id)}
                    value={value}
                    style={{ width: '100%' }}
                    onChange={nextValue => updateLevelField(record.id, 'expiration', nextValue)}
                >
                    {expirationOptions.map(item => (
                        <Select.Option key={item.value} value={item.value}>
                            {item.label}
                        </Select.Option>
                    ))}
                </Select>
            ),
        },
        {
            title: '操作',
            width: 150,
            render: (_value, record) =>
                !access.readOnly ? (
                    <>
                        {canDeleteMemberLevel(record, levels) ? (
                            <Auth perssion={20} onClick={() => onDelete(record)}>
                                <span className="action danger">删除</span>
                            </Auth>
                        ) : null}
                    </>
                ) : (
                    '/'
                ),
        },
    ]

    const saveButton = (
        <Auth perssion={20} onClick={saveDisabled ? undefined : () => saveChangedLevels()}>
            <Button types="primary" loading={saving} disabled={saveDisabled}>
                保存
            </Button>
        </Auth>
    )
    const addButton =
        access.readOnly || !nextLevelName ? null : (
            <Auth perssion={20} onClick={() => setVisible(true)}>
                <Button types="primary">新增等级</Button>
            </Auth>
        )
    const addLevelModal = visible ? (
        <SgModal
            title="新增会员等级"
            isShow
            type="success"
            loading={adding}
            onConfirm={onAdd}
            onClose={() => setVisible(false)}
            onCancel={() => setVisible(false)}
            okText="确认"
            cancelText="取消"
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ name: nextLevelName, experience_deduction: 0, expiration: 0 }}
            >
                <Form.Item
                    name="name"
                    label="等级名称"
                    rules={[
                        { required: true, message: '请输入等级名称' },
                        { max: 4, message: '会员等级名称不能超过4个字符' },
                        {
                            pattern: /^[\u4e00-\u9fa5a-zA-Z0-9]+$/,
                            message: '会员等级名称只允许中文、英文字母、数字',
                        },
                        {
                            validator: (_, value) => {
                                const trimmed = (value || '').trim()
                                if (trimmed && levels.some(item => (item.name || '').trim() === trimmed)) {
                                    return Promise.reject(new Error('会员等级名称重复'))
                                }
                                return Promise.resolve()
                            },
                        },
                    ]}
                >
                    <Input placeholder="最多4个字符" />
                </Form.Item>
                <Form.Item
                    name="experience_threshold"
                    label="成长值门槛"
                    rules={[{ required: true, message: '请输入成长值门槛' }]}
                >
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="请输入成长值门槛" />
                </Form.Item>
                <Form.Item name="experience_deduction" label="成长值扣减" initialValue={0}>
                    <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="请输入成长值扣减" />
                </Form.Item>
                <Form.Item
                    name="expiration"
                    label="有效期"
                    initialValue={0}
                    rules={[{ required: true, message: '请选择有效期' }]}
                >
                    <Select>
                        {expirationOptions.map(item => (
                            <Select.Option key={item.value} value={item.value}>
                                {item.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </SgModal>
    ) : null
    const levelContent = (
        <SettingGrid>
            <SettingPanel
                title="等级规则"
                desc="最多五级。修改后点击右上角「保存」。"
                extra={
                    <div className="member-level-panel-actions">
                        {addButton}
                        {saveButton}
                    </div>
                }
                wide
            >
                <SettingPreview
                    items={[
                        { label: '会员等级', value: `${levels.length} 个` },
                        {
                            label: '最高成长值门槛',
                            value: levels.length
                                ? Math.max(...levels.map(level => Number(level.experience_threshold || 0)))
                                : '--',
                        },
                        { label: '升级方式', value: '按成长值自动升级' },
                    ]}
                />
                <SettingTableContainer className="member-basic-setting__panel">
                    <Table
                        bordered
                        rowKey="id"
                        loading={listReq.loading}
                        columns={columns}
                        dataSource={levels}
                        pagination={false}
                    />
                </SettingTableContainer>
            </SettingPanel>
        </SettingGrid>
    )

    if (props.dialog) {
        return (
            <div className="member-level-editor member-level-editor--dialog">
                <div className="member-level-editor__toolbar">
                    <span>修改等级后，会员将按新的成长值门槛计算等级。</span>
                </div>
                <div className="member-level-editor__body">{levelContent}</div>
                <div className="member-level-editor__footer">
                    <span>修改设置后点击右上角「保存」</span>
                </div>
                {addLevelModal}
            </div>
        )
    }

    return (
        <SettingPage
            title="会员等级"
            readOnly={access.readOnly}
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            append={addLevelModal}
        >
            {levelContent}
        </SettingPage>
    )
}

export default MemberLevel
